import { Card, CardContent } from "@/components/ui/card";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthProvider";
import {
    BuscarComprasUltimoSorteioDto,
    PremioConferenciaDto,
} from "@/dtos/BuscarComprasUltimoSorteioDto";
import CompraService from "@/services/CompraService";
import { labelPlural, labelSingular } from "@/utils/copyTituloBilhete";
import clsx from "clsx";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

interface TituloConferencia {
    id: number;
    numero: string;
    numeroSorte: string;
    dezenas: string;
}

const TODAS_DEZENAS = Array.from({ length: 60 }, (_, index) =>
    String(index + 1).padStart(2, "0")
);

const MENSAGEM_SEM_COMPRA = "Nenhuma compra encontrada no ultimo sorteio";

function formatarDezenas(dezenas: string | null | undefined) {
    const dezenasFormatadas: number[] = [];

    if (dezenas == null || dezenas == undefined) {
        return [];
    }

    for (let index = 0; index < dezenas.length; index += 2) {
        const dezena = dezenas.slice(index, index + 2);
        dezenasFormatadas.push(Number(dezena));
    }

    return dezenasFormatadas
        .map((dezena) => `${dezena}`.padStart(2, "0"))
        .sort((a, b) => Number(a) - Number(b));
}

function contarAcertos(dezenas: string[], dezenasConferidas: Set<string>) {
    return dezenas.filter((dezena) => dezenasConferidas.has(dezena)).length;
}

function montarTitulosDeCompras(
    dados: BuscarComprasUltimoSorteioDto
): TituloConferencia[] {
    return dados.Compras.flatMap((compra) =>
        compra.Cautelas.flatMap((cautela) =>
            cautela.Titulos.map((titulo) => ({
                id: titulo.IdTitulo,
                numero: titulo.Numero,
                numeroSorte: titulo.NumeroSorte,
                dezenas: titulo.Dezenas,
            }))
        )
    );
}

function dezenasDoPremio(premio: PremioConferenciaDto | undefined) {
    return new Set(formatarDezenas(premio?.Dezenas));
}

export function ConferirResultadoPage() {
    const { isAuthorized } = useAuth();
    const navigate = useNavigate();
    const { toast } = useToast();

    const [loading, setLoading] = useState(true);
    const [erro, setErro] = useState<string | null>(null);
    const [dados, setDados] = useState<BuscarComprasUltimoSorteioDto | null>(null);
    const [premioSelecionadoId, setPremioSelecionadoId] = useState<number | null>(
        null
    );
    const [dezenasConferidas, setDezenasConferidas] = useState<Set<string>>(
        () => new Set()
    );

    const listaTitulosRef = useRef<HTMLDivElement>(null);
    const ordemAnteriorRef = useRef<string | null>(null);

    const titulos = useMemo(
        () => (dados ? montarTitulosDeCompras(dados) : []),
        [dados]
    );

    const premioSelecionado = useMemo(
        () => dados?.Premios.find((p) => p.IdPremio === premioSelecionadoId),
        [dados, premioSelecionadoId]
    );

    const possuiResultadoOficial = dados?.PossuiResultadoOficial === true;

    const titulosOrdenados = useMemo(() => {
        return [...titulos].sort((a, b) => {
            const acertosA = contarAcertos(
                formatarDezenas(a.dezenas),
                dezenasConferidas
            );
            const acertosB = contarAcertos(
                formatarDezenas(b.dezenas),
                dezenasConferidas
            );

            if (acertosB !== acertosA) {
                return acertosB - acertosA;
            }

            return a.id - b.id;
        });
    }, [titulos, dezenasConferidas]);

    const ordemTitulos = useMemo(
        () => titulosOrdenados.map((titulo) => titulo.id).join(","),
        [titulosOrdenados]
    );

    useEffect(() => {
        if (!isAuthorized) {
            navigate("/Acessar?redirect=conferir-resultado");
            return;
        }

        buscarCompras();
    }, [isAuthorized]);

    useLayoutEffect(() => {
        if (!listaTitulosRef.current) {
            return;
        }

        if (
            ordemAnteriorRef.current !== null &&
            ordemAnteriorRef.current !== ordemTitulos
        ) {
            listaTitulosRef.current.scrollLeft = 0;
        }

        ordemAnteriorRef.current = ordemTitulos;
    }, [ordemTitulos]);

    async function buscarCompras() {
        setLoading(true);
        setErro(null);

        try {
            const result = await CompraService.buscarComprasUltimoSorteio();
            const response = result.Data;

            const temTitulos = response.Compras.some((compra) =>
                compra.Cautelas.some((cautela) => cautela.Titulos.length > 0)
            );

            if (!temTitulos) {
                setErro(MENSAGEM_SEM_COMPRA);
                setDados(null);
                toast({
                    variant: "destructive",
                    description: MENSAGEM_SEM_COMPRA,
                    duration: 4000,
                });
                return;
            }

            setDados(response);

            const primeiroPremioId = response.Premios[0]?.IdPremio ?? null;
            setPremioSelecionadoId(primeiroPremioId);
            aplicarPremio(response, primeiroPremioId);
        } catch {
            setErro(MENSAGEM_SEM_COMPRA);
            setDados(null);
            toast({
                variant: "destructive",
                description: MENSAGEM_SEM_COMPRA,
                duration: 4000,
            });
        } finally {
            setLoading(false);
        }
    }

    function aplicarPremio(
        response: BuscarComprasUltimoSorteioDto,
        idPremio: number | null
    ) {
        const premio = response.Premios.find((p) => p.IdPremio === idPremio);

        if (response.PossuiResultadoOficial) {
            setDezenasConferidas(dezenasDoPremio(premio));
        } else {
            setDezenasConferidas(new Set());
        }
    }

    function handleTrocarPremio(idPremioStr: string) {
        if (!dados) {
            return;
        }

        const idPremio = Number(idPremioStr);
        setPremioSelecionadoId(idPremio);
        aplicarPremio(dados, idPremio);
    }

    function handleSelecionarDezena(dezena: string) {
        if (possuiResultadoOficial) {
            return;
        }

        setDezenasConferidas((atual) => {
            const proximo = new Set(atual);

            if (proximo.has(dezena)) {
                proximo.delete(dezena);
            } else {
                proximo.add(dezena);
            }

            return proximo;
        });
    }

    if (!isAuthorized) {
        return null;
    }

    if (loading) {
        return (
            <div className="mx-2 my-5">
                <div className="flex w-full justify-center mt-4">
                    Carregando informações do sorteio...
                </div>
                <div className="flex w-full justify-center mt-5 mb-10 gap-4">
                    <Skeleton className="h-80 w-3/4 hidden md:flex lg:w-full" />
                    <Skeleton className="h-80 w-3/4 hidden md:flex lg:w-full" />
                    <Skeleton className="h-80 w-3/4 lg:w-full" />
                </div>
            </div>
        );
    }

    if (erro || !dados) {
        return (
            <div className="mx-2 my-5 text-center space-y-4">
                <h1 className="text-primary font-semibold text-xl">
                    Conferir Resultados
                </h1>
                <p className="text-muted-foreground">{erro ?? MENSAGEM_SEM_COMPRA}</p>
            </div>
        );
    }

    return (
        <div className="mx-2 my-5 space-y-6">
            <div className="text-center">
                <h1 className="text-primary font-semibold text-xl">
                    Conferir Resultados
                </h1>
                <p className="text-sm text-muted-foreground mt-1">
                    Sorteio {dados.DataUltimoSorteio}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                    {possuiResultadoOficial
                        ? "Resultado oficial disponível. Selecione o prêmio para conferir."
                        : `Toque nas dezenas sorteadas para conferir seus ${labelPlural().toLowerCase()}`}
                </p>
            </div>

            {dados.Premios.length > 0 && (
                <div className="flex self-center items-center justify-center m-4 gap-2">
                    <span className="text-primary font-semibold text-xl">
                        Prêmio:
                    </span>
                    <Select
                        value={
                            premioSelecionadoId != null
                                ? `${premioSelecionadoId}`
                                : undefined
                        }
                        onValueChange={handleTrocarPremio}
                    >
                        <SelectTrigger className="w-62 text-primary font-semibold text-start">
                            <SelectValue placeholder="Selecione o prêmio" />
                        </SelectTrigger>
                        <SelectContent>
                            {dados.Premios.map((premio, index) => (
                                <SelectItem
                                    key={premio.IdPremio}
                                    value={`${premio.IdPremio}`}
                                >
                                    {index + 1}º — {premio.Descricao}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            )}

            <section>
                <h2 className="text-primary font-semibold text-lg mb-3">
                    Meus {labelPlural()}
                </h2>

                <div
                    ref={listaTitulosRef}
                    className="flex gap-4 overflow-x-auto pb-2 [overflow-anchor:none]"
                >
                    {titulosOrdenados.map((titulo) => {
                        const dezenasTitulo = formatarDezenas(titulo.dezenas);
                        const qtdAcertos = contarAcertos(
                            dezenasTitulo,
                            dezenasConferidas
                        );

                        return (
                            <Card
                                key={titulo.id}
                                className="border-2 shrink-0 w-72"
                            >
                                <CardContent className="p-3">
                                    <p className="text-primary font-semibold">
                                        {labelSingular()} nº: {titulo.numero}
                                    </p>

                                    <div className="text-primary font-semibold">
                                        Numero da Sorte:&nbsp;
                                        <span>{titulo.numeroSorte}</span>
                                    </div>

                                    <p className="text-primary font-semibold mt-2">
                                        {qtdAcertos} acerto
                                        {qtdAcertos !== 1 ? "s" : ""}
                                    </p>

                                    <div className="flex flex-col items-center mt-2">
                                        <div className="flex flex-wrap justify-center w-72 select-none">
                                            {dezenasTitulo.map((dezena, index) => {
                                                const acertou =
                                                    dezenasConferidas.has(dezena);

                                                return (
                                                    <span
                                                        key={`${titulo.id}-${dezena}-${index}`}
                                                        className={clsx(
                                                            "text-center tabular-nums rounded-full m-1 h-12 w-12 p-3 border-2 shadow-md flex items-center justify-center",
                                                            {
                                                                "bg-primary text-primary-foreground border-primary":
                                                                    acertou,
                                                            }
                                                        )}
                                                    >
                                                        {dezena}
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            </section>

            <section>
                <h2 className="text-primary font-semibold text-lg mb-3 text-center">
                    Dezenas sorteadas
                    {premioSelecionado
                        ? ` — ${premioSelecionado.Descricao}`
                        : ""}
                </h2>

                <Card className="border-2">
                    <CardContent className="p-3">
                        <div className="flex justify-center mt-2">
                            <div className="flex flex-wrap justify-center w-full max-w-xl select-none">
                                {TODAS_DEZENAS.map((dezena) => {
                                    const selecionada =
                                        dezenasConferidas.has(dezena);

                                    return (
                                        <span
                                            key={dezena}
                                            onClick={() =>
                                                handleSelecionarDezena(dezena)
                                            }
                                            className={clsx(
                                                "text-xs text-center tabular-nums rounded-full m-1 h-10 w-10 flex items-center justify-center border-2 shadow-md",
                                                {
                                                    "bg-primary text-primary-foreground border-primary":
                                                        selecionada,
                                                    "cursor-pointer":
                                                        !possuiResultadoOficial,
                                                    "cursor-not-allowed opacity-90":
                                                        possuiResultadoOficial,
                                                }
                                            )}
                                        >
                                            {dezena}
                                        </span>
                                    );
                                })}
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </section>
        </div>
    );
}
